import { Controller } from "@hotwired/stimulus"

// Live cover-title editor: manages the per-line rows (add/remove, with per-line
// font + bold/italic + alignment), serializes them into a hidden JSON field for
// the form, and renders a live preview of the whole cover (color + pattern +
// designed title) so the user sees the book as they build it. CSP-safe — all
// DOM is built via the API, never inline scripts/styles.
const COVER_BG = {
  cream: "bg-cream-100", sage: "bg-emerald-100", sky: "bg-sky-100",
  blush: "bg-rose-100", butter: "bg-amber-100", slate: "bg-slate-200", purple: "bg-purple-100"
}
const INK = {
  cream: "text-amber-400", sage: "text-emerald-400", sky: "text-sky-400",
  blush: "text-rose-400", butter: "text-amber-400", slate: "text-slate-400", purple: "text-purple-400"
}
const SIZES = { xs: "text-xs", s: "text-sm", m: "text-lg", l: "text-2xl", xl: "text-3xl", xxl: "text-4xl" }

export default class extends Controller {
  static targets = ["row", "rows", "rowTemplate", "linesField", "preview", "previewPattern", "previewPhoto", "previewLines", "overlay", "overlayControls", "addButton", "opacity"]

  connect() {
    this.sync()
  }

  addLine() {
    if (this.rowTargets.length >= 5) return
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    this.rowsTarget.appendChild(row)
    this.sync()
  }

  removeLine(event) {
    event.target.closest("[data-cover-editor-target='row']")?.remove()
    this.sync()
  }

  // Alignment is a 3-button segment per row backed by a hidden field, so it
  // survives row cloning (radios would need per-row name groups).
  setAlign(event) {
    const button = event.currentTarget
    const row = button.closest("[data-cover-editor-target='row']")
    row.querySelector("[data-field='align']").value = button.dataset.align
    row.querySelectorAll("[data-align]").forEach((b) => b.setAttribute("aria-pressed", String(b === button)))
    this.sync()
  }

  // Read every row, write the JSON the form submits, then repaint the preview.
  sync() {
    const lines = this.rowTargets.map((row) => {
      this.applyFontStyle(row)
      return {
        text: this.field(row, "text").value,
        font: this.field(row, "font").value,
        size: this.field(row, "size").value,
        bold: this.field(row, "bold").checked,
        italic: this.field(row, "italic").checked,
        align: this.field(row, "align").value,
        color: this.field(row, "color")?.value || "#111827"
      }
    }).filter((line) => line.text.trim() !== "")

    this.linesFieldTarget.value = JSON.stringify(lines)
    this.toggleAddButton()
    this.renderPreview(lines)
  }

  renderPreview(lines) {
    const color = this.checkedValue("cover_color") || "cream"
    const style = this.checkedValue("cover_style") || "photo"
    const pattern = this.checkedValue("cover_pattern") || "plain"

    this.previewTarget.className = this.previewTarget.dataset.baseClass + " " + (COVER_BG[color] || COVER_BG.cream)

    const showPattern = style === "pattern" && pattern !== "plain"
    this.previewPatternTarget.className = showPattern
      ? `absolute inset-0 cover-pattern-${pattern} ${INK[color] || INK.cream} opacity-30`
      : "hidden"

    // Photo style: mirror the uploaded cover image (existing, or one just dropped).
    const photoUrl = style === "photo" ? this.coverPhotoUrl() : null
    if (this.hasPreviewPhotoTarget) {
      this.previewPhotoTarget.src = photoUrl || ""
      this.previewPhotoTarget.classList.toggle("hidden", !photoUrl)
    }
    // Overlay off in Photo style: hide the binding + title plate (preview) AND the
    // title/color editing controls — the uploaded photo becomes the whole cover.
    const overlayOff = style === "photo" && !this.overlayEnabled()
    if (this.hasOverlayTarget) this.overlayTarget.classList.toggle("hidden", overlayOff)
    this.overlayControlsTargets.forEach((el) => el.classList.toggle("hidden", overlayOff))

    // Box opacity rides the same --cover-opacity custom property the rendered
    // covers use (0-1 alpha), set on the plate so .cover-plate-box picks it up.
    if (this.hasOpacityTarget) {
      this.previewLinesTarget.style.setProperty("--cover-opacity", `${this.opacityTarget.value / 100}`)
    }

    this.previewLinesTarget.replaceChildren()
    lines.forEach((line) => {
      const span = document.createElement("span")
      span.textContent = line.text
      span.className = [
        "cover-title-line block leading-tight",
        SIZES[line.size] || SIZES.m,
        `cover-font-${line.font.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        `text-${line.align}`,
        line.bold ? "font-bold" : "font-semibold",
        line.italic ? "italic" : ""
      ].join(" ")
      span.style.setProperty("--line-color", line.color || "#111827")
      this.previewLinesTarget.appendChild(span)
    })
  }

  field(row, name) {
    return row.querySelector(`[data-field='${name}']`)
  }

  // Render the font <select> in the font it currently points at (the options
  // are already class-styled server-side) — matches the profile font picker.
  applyFontStyle(row) {
    const select = this.field(row, "font")
    ;[ ...select.classList ].filter((c) => c.startsWith("cover-font-")).forEach((c) => select.classList.remove(c))
    select.classList.add(`cover-font-${select.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`)
  }

  checkedValue(name) {
    return this.element.querySelector(`input[name*='${name}']:checked`)?.value
  }

  // The uploaded cover image lives in the photo-upload section (existing variant
  // URL, or a data: URL after a fresh drop). Read it so the preview matches.
  coverPhotoUrl() {
    const img = this.element.querySelector("[data-cover-style-target='photoSection'] img")
    return img?.getAttribute("src") || null
  }

  // The "Show title & color overlay" checkbox (absent on covers that don't offer
  // it → overlay stays on).
  overlayEnabled() {
    const box = this.element.querySelector("input[type=checkbox][name*='cover_overlay']")
    return box ? box.checked : true
  }

  toggleAddButton() {
    if (this.hasAddButtonTarget) this.addButtonTarget.classList.toggle("hidden", this.rowTargets.length >= 5)
  }
}
